import React, { useState, useEffect } from 'react';
import { Project as BackendProject, projectApi, ProjectStatus } from '../services/projectApi';
import { ppeApi, PPEData } from '../services/ppeApi';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import NewProjectModal from '../components/NewProjectModal';
import PositionManagementModal from '../components/PositionManagementModal';

const formatPercentage = (value: number) => {
  if (isNaN(value)) return '0.0%';
  return `${(value * 100).toFixed(1)}%`;
};

const PPEManagement: React.FC = () => {
  const { user, userRole } = useAuth();
  const { showToast, showConfirm } = useAlert();
  const [projects, setProjects] = useState<BackendProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [filteredProjects, setFilteredProjects] = useState<BackendProject[]>([]);
  const [sortField, setSortField] = useState<string>('startDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 연도 필터 상태
  const [selectedYear, setSelectedYear] = useState<string>('all');

  useEffect(() => {
    // 사용자 정보와 역할이 로딩된 후 프로젝트 조회
    if (userRole && user) {
      fetchProjects();
    }
  }, [userRole, user]);

  const fetchProjects = async () => {
    try {
      setLoading(true);

      let data: BackendProject[];

      // PROJECT_MANAGER 역할인 경우 자신이 PM인 프로젝트만 조회
      if (userRole === 'PROJECT_MANAGER' && user?.id) {
        data = await projectApi.getMyProjects();
      } else {
        // 다른 역할은 모든 프로젝트 조회
        data = await projectApi.getAll();
      }

      setProjects(data);

      // 프로젝트 시작일 기준으로 정렬 (최신순)
      const sortedData = [...data].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
      setFilteredProjects(sortedData);
      
      // 각 프로젝트의 PPE 데이터도 함께 가져오기
      const ppeMap = new Map<number, PPEData>();
      for (const project of data) {
        try {
          const ppeData = await ppeApi.getByProjectId(project.id);
          if (ppeData) {
            ppeMap.set(project.id, ppeData);
          }
        } catch {
          // PPE 데이터가 없는 프로젝트는 무시
        }
      }
      setPpeDataMap(ppeMap);
    } catch (error) {
      showToast('프로젝트 데이터를 불러오는데 실패했습니다: ' + (error as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const [selectedProject, setSelectedProject] = useState<BackendProject | undefined>();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [ppeDataMap, setPpeDataMap] = useState<Map<number, PPEData>>(new Map());

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('ko-KR').format(Math.round(num));
  };


  const openDetailModal = (project: BackendProject) => {
    setSelectedProject(project);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedProject(undefined);
  };

  const handleSavePPE = async (updatedData: any) => {
    try {
      // 명시적으로 업데이트 타입인 경우에만 페이지 새로고침만 (실제로는 사용되지 않는 조건)
      if (updatedData.type === 'update' && !updatedData.manpowerItems && !updatedData.revenue) {
        await fetchProjects();
        closeDetailModal();
        return;
      }
      
      // 새 프로젝트 생성은 handleSaveNewProject에서 처리
      if (updatedData.ppeData) {
        await handleSaveNewProject(updatedData);
        return;
      }
      
      // PPE 데이터 업데이트 - selectedProject가 있고 updatedData에 PPE 필드들이 있는 경우
      if (selectedProject && (updatedData.revenue !== undefined || updatedData.manpowerItems)) {
        // 기존 PPE 데이터 조회
        let existingPPEId: number | undefined;
        try {
          const existingPPE = await ppeApi.getByProjectId(selectedProject.id);
          existingPPEId = existingPPE.id;
        } catch {
          // PPE 데이터가 없으면 새로 생성
        }

        if (existingPPEId) {
          // 기존 PPE 업데이트
          await ppeApi.update(existingPPEId, updatedData);
        } else {
          // 새 PPE 생성
          await ppeApi.create(selectedProject.id, updatedData);
        }
        
        // 프로젝트 목록 새로고침
        await fetchProjects();
        closeDetailModal();
        return;
      }
      
    } catch (error) {
      showToast('저장에 실패했습니다: ' + (error as Error).message, 'error');
    }
  };

  const handleSaveNewProject = async (projectWithPPE: any) => {
    try {
      
      // 프로젝트 기본 정보로 프로젝트 생성
      const projectData = {
        name: projectWithPPE.projectName || '',
        client: 'New Client', // 기본값, 추후 모달에서 입력받도록 수정 필요
        startDate: projectWithPPE.startDate || new Date().toISOString().split('T')[0],
        endDate: projectWithPPE.endDate || new Date(new Date().setMonth(new Date().getMonth() + (projectWithPPE.months || 12))).toISOString().split('T')[0],
        pm: projectWithPPE.pm || user?.name || 'TBD',
        contractValue: projectWithPPE.ppeData?.revenue || 0,
        status: ProjectStatus.PLANNED
      };
      
      const createdProject = await projectApi.create(projectData);
      
      // PPE 데이터가 있으면 저장
      if (projectWithPPE.ppeData) {
        await ppeApi.create(createdProject.id, projectWithPPE.ppeData);
      }
      
      await fetchProjects(); // 프로젝트 목록 새로고침
      setIsNewModalOpen(false);
      showToast('새 프로젝트와 PPE 데이터가 성공적으로 저장되었습니다.', 'success');
    } catch (error) {
      showToast('프로젝트 생성 중 오류가 발생했습니다: ' + (error as Error).message, 'error');
    }
  };

  // 정렬 함수
  const handleSort = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    const sortedProjects = [...filteredProjects].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (field) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'startDate':
          aValue = new Date(a.startDate).getTime();
          bValue = new Date(b.startDate).getTime();
          break;
        case 'days':
          const aStartDate = new Date(a.startDate);
          const aEndDate = new Date(a.endDate);
          const bStartDate = new Date(b.startDate);
          const bEndDate = new Date(b.endDate);
          // 일수를 30으로 나누어 월수로 변환
          aValue = ((aEndDate.getTime() - aStartDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 30;
          bValue = ((bEndDate.getTime() - bStartDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 30;
          break;
        case 'pm':
          aValue = (a.pm || '').toLowerCase();
          bValue = (b.pm || '').toLowerCase();
          break;
        case 'revenue':
          aValue = a.contractValue || 0;
          bValue = b.contractValue || 0;
          break;
        case 'grossIncome':
          const aPpeData = ppeDataMap.get(a.id);
          const bPpeData = ppeDataMap.get(b.id);
          aValue = aPpeData?.grossIncome || 0;
          bValue = bPpeData?.grossIncome || 0;
          break;
        case 'profit':
          const aPpeDataProfit = ppeDataMap.get(a.id);
          const bPpeDataProfit = ppeDataMap.get(b.id);
          aValue = aPpeDataProfit?.profit || 0;
          bValue = bPpeDataProfit?.profit || 0;
          break;
        case 'profitRate':
          const aPpeRate = ppeDataMap.get(a.id);
          const bPpeRate = ppeDataMap.get(b.id);
          const aRevenue = a.contractValue || 0;
          const bRevenue = b.contractValue || 0;
          aValue = aRevenue > 0 ? (aPpeRate?.profit || 0) / aRevenue : 0;
          bValue = bRevenue > 0 ? (bPpeRate?.profit || 0) / bRevenue : 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return newDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return newDirection === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    setFilteredProjects(sortedProjects);
  };

  // 정렬 아이콘 표시
  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <span className="ml-1 text-gray-400">↕</span>;
    }
    return sortDirection === 'asc' 
      ? <span className="ml-1 text-indigo-600">↑</span>
      : <span className="ml-1 text-indigo-600">↓</span>;
  };

  // 연도 목록 추출 (프로젝트 시작일 기준)
  const availableYears = React.useMemo(() => {
    const years = new Set<number>();
    filteredProjects.forEach((project) => {
      const year = new Date(project.startDate).getFullYear();
      years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a); // 최신 연도부터
  }, [filteredProjects]);

  // 연도별 필터링
  const filteredByYear = React.useMemo(() => {
    if (selectedYear === 'all') {
      return filteredProjects;
    }
    return filteredProjects.filter((project) => {
      const year = new Date(project.startDate).getFullYear();
      return year.toString() === selectedYear;
    });
  }, [filteredProjects, selectedYear]);

  // 페이지네이션 계산 (연도 필터링된 데이터 기준)
  const totalPages = Math.ceil(filteredByYear.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = filteredByYear.slice(startIndex, endIndex);

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 연도 필터 변경 핸들러
  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    setCurrentPage(1); // 필터 변경 시 첫 페이지로 이동
  };

  // 프로젝트 삭제 함수
  const handleDeleteProject = async (projectId: number, projectName: string) => {
    const confirmed = await showConfirm(`"${projectName}" 프로젝트를 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없으며, 관련된 PPE 데이터도 함께 삭제됩니다.`);
    if (!confirmed) {
      return;
    }

    try {
      // 프로젝트 삭제 (백엔드에서 CASCADE로 PPE 데이터도 함께 삭제됨)
      await projectApi.delete(projectId);

      // 3. 로컬 상태에서도 제거
      const updatedProjects = projects.filter(p => p.id !== projectId);
      const updatedFilteredProjects = filteredProjects.filter(p => p.id !== projectId);
      setProjects(updatedProjects);
      setFilteredProjects(updatedFilteredProjects);

      // 4. PPE 데이터 맵에서도 제거
      const newPpeDataMap = new Map(ppeDataMap);
      newPpeDataMap.delete(projectId);
      setPpeDataMap(newPpeDataMap);

      showToast(`"${projectName}" 프로젝트가 성공적으로 삭제되었습니다.`, 'success');
    } catch (error) {
      showToast('프로젝트 삭제 중 오류가 발생했습니다: ' + (error as Error).message, 'error');
    }
  };

  return (
    <>
      <main className="flex-1 overflow-y-auto p-6 lg:p-8">
        <header className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-800">PPE</h1>
              <p className="text-slate-500 mt-1">프로젝트별 매출, 원가, 손익을 종합적으로 관리합니다.</p>
            </div>
            {userRole !== 'PROJECT_MANAGER' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsPositionModalOpen(true)}
                  className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  직급관리
                </button>
                <button
                  onClick={() => setIsNewModalOpen(true)}
                  className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  + 새 프로젝트
                </button>
              </div>
            )}
          </div>
          {filteredProjects.length > 0 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-600">
                총 {filteredByYear.length}개 프로젝트
              </span>
              <span className="text-sm text-slate-500">
                {startIndex + 1}-{Math.min(endIndex, filteredByYear.length)} / {filteredByYear.length}
              </span>
            </div>
          )}
        </header>

        {/* 연도 필터 */}
        {filteredProjects.length > 0 && (
          <div className="mb-4 flex items-center space-x-2">
            <span className="text-sm font-medium text-slate-600">연도:</span>
            <button
              onClick={() => handleYearChange('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                selectedYear === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
              }`}
            >
              전체
            </button>
            {availableYears.map((year) => (
              <button
                key={year}
                onClick={() => handleYearChange(year.toString())}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedYear === year.toString()
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  <th 
                    className="px-6 py-3 font-medium text-left cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      프로젝트명
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 font-medium text-left cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('startDate')}
                  >
                    <div className="flex items-center">
                      기간
                      {getSortIcon('startDate')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 font-medium text-center cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('days')}
                  >
                    <div className="flex items-center justify-center">
                      기간(월)
                      {getSortIcon('days')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 font-medium text-left cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('pm')}
                  >
                    <div className="flex items-center">
                      PM
                      {getSortIcon('pm')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 font-medium text-right cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('revenue')}
                  >
                    <div className="flex items-center justify-end">
                      Revenue
                      {getSortIcon('revenue')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 font-medium text-right cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('grossIncome')}
                  >
                    <div className="flex items-center justify-end">
                      Gross Income
                      {getSortIcon('grossIncome')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 font-medium text-right cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('profit')}
                  >
                    <div className="flex items-center justify-end">
                      Profit
                      {getSortIcon('profit')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 font-medium text-center cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('profitRate')}
                  >
                    <div className="flex items-center justify-center">
                      Profit Rate
                      {getSortIcon('profitRate')}
                    </div>
                  </th>
                  <th className="px-6 py-3 font-medium text-center">작업</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-8 text-center text-slate-500">
                      로딩 중...
                    </td>
                  </tr>
                ) : filteredByYear.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-8 text-center text-slate-500">
                      프로젝트가 없습니다.
                    </td>
                  </tr>
                ) : (
                  currentProjects.map((project) => {
                    const period = `${new Date(project.startDate).toLocaleDateString('ko-KR')} ~ ${new Date(project.endDate).toLocaleDateString('ko-KR')}`;
                    const startDate = new Date(project.startDate);
                    const endDate = new Date(project.endDate);
                    // 월수 계산: 일수를 30으로 나누어 월수로 변환 (소수점 첫째자리까지)
                    const daysDiff = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                    const monthsDiff = (daysDiff / 30).toFixed(1);
                    
                    // PPE 데이터가 있는지 확인하고 값 추출
                    const ppeData = ppeDataMap.get(project.id);
                    const revenue = project.contractValue || 0;
                    const grossIncome = ppeData?.grossIncome || 0;
                    const profit = ppeData?.profit || 0;
                    const profitRate = revenue > 0 ? profit / revenue : 0;
                    
                    return (
                      <tr
                        key={project.id}
                        className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer"
                        onClick={() => openDetailModal(project)}
                      >
                        <td className="px-6 py-4 font-semibold text-slate-900">{project.name}</td>
                        <td className="px-6 py-4 text-sm">{period}</td>
                        <td className="px-6 py-4 text-center font-semibold">{monthsDiff}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{project.pm || '-'}</td>
                        <td className="px-6 py-4 text-right font-semibold">
                          {formatCurrency(typeof revenue === 'string' ? parseFloat(revenue) : revenue)}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold">
                          {grossIncome !== 0 ? formatCurrency(grossIncome) : '-'}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold">
                          {formatCurrency(profit)}
                        </td>
                        <td className="px-6 py-4 text-center font-semibold">
                          {formatPercentage(profitRate)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(project.id, project.name);
                            }}
                            className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 hover:text-red-700 transition-colors whitespace-nowrap"
                            title="프로젝트 삭제"
                          >
                            삭제
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center mt-6 space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>

            <div className="flex space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // 페이지가 많을 때 일부만 표시
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-indigo-600 text-white'
                          : 'text-slate-700 bg-white border border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  page === currentPage - 3 ||
                  page === currentPage + 3
                ) {
                  return (
                    <span key={page} className="px-2 py-2 text-slate-400">
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </main>

      <NewProjectModal
        isOpen={isDetailModalOpen}
        onClose={closeDetailModal}
        project={selectedProject}
        onSave={handleSavePPE as any}
      />

      <NewProjectModal
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
        onSave={handleSaveNewProject}
      />

      <PositionManagementModal
        isOpen={isPositionModalOpen}
        onClose={() => setIsPositionModalOpen(false)}
      />
    </>
  );
};

export default PPEManagement;